import { useMemo, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Checkbox, Chip, ChoiceRow } from './choice'
import { SearchInput } from './search-input'

/**
 * Escolha em lista longa (componente 04): busca em cima, as escolhas viram `Chip` acima da lista,
 * os itens vêm agrupados com o contador de cada grupo e a linha inteira é o alvo.
 * Com `multiple={false}` a lista guarda uma escolha só: escolher outra troca.
 */

export interface PickListItem {
  id: string
  label: string
  /** Linha de apoio abaixo do rótulo. */
  description?: ReactNode
  /** Nome do grupo; itens sem grupo vêm primeiro, sem título. */
  group?: string
  /** Outros termos que a busca alcança (sinônimos). */
  keywords?: readonly string[]
  /** Não pode ser escolhido; `note` diz por quê. */
  disabled?: boolean
  /** Conteúdo à direita da linha ("já no seu catálogo", etiqueta de status). */
  note?: ReactNode
}

export interface PickListProps {
  items: readonly PickListItem[]
  value: readonly string[]
  onChange: (ids: string[]) => void
  multiple?: boolean
  /** Nome acessível da lista. */
  label: string
  searchPlaceholder?: string
  /** "4 SELECIONADOS". Recebe a contagem. */
  selectedLabel?: (count: number) => string
  /** O que mostrar quando a busca não acha nada. Recebe o texto buscado. */
  emptySearch?: (query: string) => ReactNode
  /** Foco na busca ao montar (lista com mais de 12 itens). */
  autoFocus?: boolean
  className?: string
}

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function PickList({
  items,
  value,
  onChange,
  multiple = true,
  label,
  searchPlaceholder = 'Buscar',
  selectedLabel = (count) => `${count} ${count === 1 ? 'selecionado' : 'selecionados'}`,
  emptySearch,
  autoFocus,
  className,
}: PickListProps) {
  const [query, setQuery] = useState('')
  const selected = useMemo(() => new Set(value), [value])
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])

  const groups = useMemo(() => {
    const needle = normalize(query.trim())
    const visible = needle
      ? items.filter((item) =>
          [item.label, item.group ?? '', ...(item.keywords ?? [])].some((text) =>
            normalize(text).includes(needle),
          ),
        )
      : items
    const map = new Map<string, PickListItem[]>()
    for (const item of visible) {
      const key = item.group ?? ''
      const list = map.get(key)
      if (list) list.push(item)
      else map.set(key, [item])
    }
    return [...map.entries()]
  }, [items, query])

  const toggle = (id: string) => {
    if (!multiple) return onChange(selected.has(id) ? [] : [id])
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  return (
    <div data-slot="pick-list" className={cn('flex flex-col gap-3', className)}>
      {value.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1" aria-live="polite">
            {selectedLabel(value.length)}
          </span>
          {value.map((id) => (
            <Chip key={id} onRemove={() => toggle(id)}>
              {byId.get(id)?.label ?? id}
            </Chip>
          ))}
        </div>
      ) : null}
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        autoFocus={autoFocus}
        className="min-w-0"
      />
      <div
        role="group"
        aria-label={label}
        // `relative`: tudo o que é absoluto dentro da lista é recortado pela rolagem dela.
        className="border-hairline bg-surface relative max-h-[420px] overflow-y-auto border"
      >
        {groups.length === 0 ? (
          <div className="font-body text-body text-secondary px-4 py-6 font-light">
            {emptySearch ? emptySearch(query.trim()) : 'Nada encontrado para esta busca.'}
          </div>
        ) : (
          groups.map(([group, groupItems]) => (
            <div key={group || '—'}>
              {group ? (
                <div className="border-hairline bg-pampas eyebrow sticky top-0 z-[1] flex items-baseline justify-between border-b px-4 py-[10px]">
                  <span>{group}</span>
                  <span className="text-label-soft tracking-normal">{groupItems.length}</span>
                </div>
              ) : null}
              {groupItems.map((item) => (
                <ChoiceRow
                  key={item.id}
                  bare
                  className={cn(item.disabled && 'cursor-default hover:bg-transparent')}
                  control={
                    <Checkbox
                      checked={selected.has(item.id)}
                      disabled={item.disabled}
                      onCheckedChange={() => toggle(item.id)}
                      aria-label={item.label}
                    />
                  }
                  description={item.description}
                  trailing={
                    item.note ? (
                      <span className="font-body text-eyebrow text-label shrink-0 font-light tracking-normal">
                        {item.note}
                      </span>
                    ) : undefined
                  }
                >
                  {item.label}
                </ChoiceRow>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
